import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useGenerationStore } from '@/stores/generation';
import { BriefCreatorOverlay } from '@/components/brief/BriefCreatorOverlay';
import { ClientCombobox } from '@/components/ClientCombobox';
import {
  AlertCircle,
  Archive,
  Book,
  ChevronRight,
  ExternalLink,
  FileText,
  GitBranch,
  Image as ImageIcon,
  Layers,
  Link2Off,
  Loader2,
  Plus,
  Search,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';

import { clsx } from 'clsx';
import type { GenerationBrief, GenerationPlaybook, AdPackStatusResponse } from '@/services/api';
import { adGenerationApi, getApiBase } from '@/services/api';

const AGENT_BASE_URL = getApiBase();


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const SOURCE_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  persona_enriched: { label: 'Persona Enriched', icon: '🧠' },
  screenshot_extraction: { label: 'Screenshot Extraction', icon: '📸' },
  freeform_input: { label: 'Freeform Input', icon: '✍️' },
  asset_backfill: { label: 'Asset Backfill', icon: '📦' },
  creative_review: { label: 'Creative Review Backfill', icon: '🔍' },
  competitor_analysis: { label: 'Competitor Analysis', icon: '🎯' },
  template_clone: { label: 'Template Clone', icon: '📋' },
  ad_pipeline: { label: 'Ad Pipeline', icon: '⚡' },
  manual: { label: 'Manual', icon: '✏️' },
};

function getSourceTypeLabel(sourceType?: string): { label: string; icon: string } {
  if (!sourceType) return { label: 'Unknown', icon: '❓' };
  return SOURCE_TYPE_LABELS[sourceType] || { label: sourceType, icon: '📄' };
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  active: 'bg-teal-50 text-teal-700 border-teal-200',
  archived: 'bg-slate-50 text-slate-400 border-slate-200',
  superseded: 'bg-amber-50 text-amber-700 border-amber-200',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        STATUS_COLORS[status] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {status}
    </span>
  );
}

function QualityBar({ value, className }: { value: number; className?: string }) {
  const percent = Math.round(value * 100);
  const color = percent < 50 ? 'bg-rose-400' : percent < 80 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div className={clsx('bg-slate-200 rounded-full overflow-hidden', className ?? 'h-1.5 w-16')}>
      <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${percent}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// List View
// ---------------------------------------------------------------------------

export function BriefsListView({
  onOpenBrief,
  embedded = false,
  groupBy = 'flat',
  searchQuery = '',
}: {
  onOpenBrief: (id: string) => void;
  embedded?: boolean;
  groupBy?: 'flat' | 'playbook';
  searchQuery?: string;
}) {
  const navigate = useNavigate();
  const {
    briefs,
    briefsTotal,
    briefsLoading,
    briefsError,
    briefFilters,
    fetchBriefs,
    setBriefFilters,
    clearBriefFilters,
    playbooks,
    playbooksTotal,
  } = useGenerationStore();

  const [searchInput, setSearchInput] = useState(briefFilters.search);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (embedded) return;
    fetchBriefs();
  }, [fetchBriefs, embedded]);

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== briefFilters.search) {
        setBriefFilters({ search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput, briefFilters.search, setBriefFilters]);

  // Client-side filtering for briefs
  const filteredBriefs = useMemo(() => {
    let result = briefs;
    const q = (searchQuery || briefFilters.search || '').toLowerCase();
    if (q) {
      result = result.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.brief_id.toLowerCase().includes(q) ||
          b.client_uid.toLowerCase().includes(q) ||
          b.persona_id.toLowerCase().includes(q)
      );
    }
    if (briefFilters.source_type) {
      result = result.filter((b) => b.provenance?.source_type === briefFilters.source_type);
    }
    // Sort by most-recently-updated first (updated_at falls back to created_at)
    return [...result].sort((a, b) => {
      const ta = a.updated_at ?? a.created_at ?? '';
      const tb = b.updated_at ?? b.created_at ?? '';
      return tb.localeCompare(ta);
    });
  }, [briefs, briefFilters.search, briefFilters.source_type, searchQuery]);

  // Client-side filtering for playbooks (status, client, search)
  const filteredPlaybooks = useMemo(() => {
    const q = (searchQuery || briefFilters.search || '').toLowerCase();
    let result = playbooks;
    if (briefFilters.client_uid) {
      result = result.filter((p) => p.client_uid === briefFilters.client_uid);
    }
    if (briefFilters.status) {
      result = result.filter((p) => p.status === briefFilters.status);
    }
    if (q) {
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.playbook_id.toLowerCase().includes(q) ||
          p.client_uid.toLowerCase().includes(q)
      );
    }
    return result;
  }, [playbooks, briefFilters.client_uid, briefFilters.status, briefFilters.search, searchQuery]);

  // Build a map: playbook_id → how many briefs reference it
  const briefsByPlaybook = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of briefs) {
      for (const pid of b.playbook_ids ?? []) {
        map[pid] = (map[pid] ?? 0) + 1;
      }
    }
    return map;
  }, [briefs]);

  // Extract unique source types for dropdown
  const uniqueSourceTypes = useMemo(
    () => [...new Set(briefs.map((b) => b.provenance?.source_type).filter(Boolean))],
    [briefs]
  );

  const hasFilters =
    briefFilters.status ||
    briefFilters.client_uid ||
    briefFilters.persona_id ||
    briefFilters.search ||
    briefFilters.source_type;

  const handleNewBrief = () => setShowCreateModal(true);

  const handleBriefCreated = (brief: { brief_id: string; client_uid: string }) => {
    setShowCreateModal(false);
    navigate(`/creative/briefs/${brief.brief_id}`);
  };

  // ── Playbook view ─────────────────────────────────────────────────────────
  if (groupBy === 'playbook') {
    const displayedPlaybooks = filteredPlaybooks;
    return (
      <>
        {briefsError && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertCircle size={16} />
            <span>{briefsError}</span>
          </div>
        )}

        {/* Summary row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
            Total Playbooks
          </span>
          <span className="text-sm font-bold text-slate-700">{playbooksTotal}</span>
          <span className="text-slate-200">·</span>
          <span className="text-[11px] text-slate-400">
            {displayedPlaybooks.filter((p) => p.status === 'active').length} active
          </span>
        </div>

        {displayedPlaybooks.length > 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Playbook
                  </th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Briefs
                  </th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Assets
                  </th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="text-right px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayedPlaybooks.map((pb) => (
                  <PlaybookRow
                    key={pb.playbook_id}
                    playbook={pb}
                    briefCount={briefsByPlaybook[pb.playbook_id] ?? 0}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
            <Book size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No playbooks found</p>
          </div>
        )}
      </>
    );
  }

  // ── Briefs (flat) view ────────────────────────────────────────────────────
  return (
    <>
      {/* Header */}
      {!embedded && (
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Creative Briefs</h1>
            <p className="text-sm text-slate-500 mt-1">
              {briefsTotal} total briefs in the system
            </p>
          </div>
          <button
            onClick={handleNewBrief}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors"
          >
            <Plus size={14} />
            New Brief
          </button>
        </div>
      )}

      {/* Error */}
      {briefsError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{briefsError}</span>
          <button onClick={fetchBriefs} className="ml-auto text-amber-800 hover:underline text-xs font-medium">
            Retry
          </button>
        </div>
      )}

      {/* Legacy Filter Bar — only in standalone mode */}
      {!embedded && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search briefs..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <select
            value={briefFilters.status}
            onChange={(e) => setBriefFilters({ status: e.target.value })}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="superseded">Superseded</option>
          </select>
          <ClientCombobox
            value={briefFilters.client_uid}
            onChange={(uid) => setBriefFilters({ client_uid: uid })}
            placeholder="All Clients"
            showAllOption
            size="sm"
            className="min-w-[180px]"
          />
          <select
            value={briefFilters.source_type}
            onChange={(e) => setBriefFilters({ source_type: e.target.value })}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
          >
            <option value="">All Sources</option>
            {uniqueSourceTypes.map((s) => (
              <option key={s} value={s!}>
                {getSourceTypeLabel(s!).label}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button
              onClick={clearBriefFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <X size={12} />
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Briefs Table */}
      {briefsLoading && briefs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <SkeletonRows count={5} />
        </div>
      ) : filteredBriefs.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Brief
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Persona
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Quality
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Downstream
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="text-right px-4 py-3 font-bold text-slate-500 uppercase tracking-wider sticky right-0 bg-slate-50 after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-slate-200">
                  Open
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredBriefs.map((brief) => (
                <BriefRow key={brief.brief_id} brief={brief} onClick={() => onOpenBrief(brief.brief_id)} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
          <FileText size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-400">No briefs found</p>
          <p className="text-[11px] text-slate-400 mt-1">
            Generate one via the MCP <code className="text-purple-500">get_creative_brief</code> tool
          </p>
        </div>
      )}

      {briefsLoading && briefs.length > 0 && (
        <p className="text-[10px] text-slate-400 font-mono text-center">refreshing...</p>
      )}

      {!embedded && (
        <BriefCreatorOverlay
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onBriefCreated={(briefId) => {
            setShowCreateModal(false);
            handleBriefCreated({ brief_id: briefId, client_uid: briefFilters.client_uid });
          }}
          defaultClientUid={briefFilters.client_uid || undefined}
          defaultPersonaId={briefFilters.persona_id || undefined}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Asset Count Badge — inline chip + portal hover popover
// ---------------------------------------------------------------------------

interface ThumbAsset {
  asset_id: string;
  media_url?: string;
  thumbnail_url?: string;
}

function isActiveStatus(s?: string) {
  const v = (s ?? '').toLowerCase();
  return v === 'running' || v === 'pending' || v === 'queued' || v === 'initializing' || v === 'generating';
}
function isCompletedStatus(s?: string) {
  const v = (s ?? '').toLowerCase();
  return v === 'completed' || v === 'success' || v === 'complete' || v === 'published' || v === 'partial';
}

function AssetCountBadge({ brief }: { brief: GenerationBrief }) {
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [visible, setVisible] = useState(false);
  const [popPos, setPopPos] = useState({ top: 0, left: 0 });
  // null = not yet fetched; [] = fetched but empty
  const [jobs, setJobs] = useState<AdPackStatusResponse[] | null>(null);
  const [thumbs, setThumbs] = useState<ThumbAsset[]>([]);
  const [fetching, setFetching] = useState(false);
  // Once we've fetched and confirmed there are truly no jobs, hide the chip
  const [confirmedEmpty, setConfirmedEmpty] = useState(false);
  const hoverTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  // After fetch: hide the badge entirely if there are genuinely no jobs
  useEffect(() => {
    if (jobs !== null && jobs.length === 0) {
      setConfirmedEmpty(true);
    }
  }, [jobs]);

  const completedJobs = (jobs ?? []).filter((j) => isCompletedStatus(j.status));
  const activeJobs = (jobs ?? []).filter((j) => isActiveStatus(j.status));
  const latestCompleted = completedJobs[0] ?? null;
  const packId = latestCompleted?.output_pack_id ?? latestCompleted?.pack_id ?? null;
  const packPath = packId
    ? `/creative/packs/${packId}${latestCompleted?.job_id ? `?job=${encodeURIComponent(latestCompleted.job_id)}` : ''}`
    : null;

  // Count: prefer the live job total, fall back to brief field
  const adsFromJobs = completedJobs.reduce((sum, j) => sum + (j.ads_generated ?? 0), 0);
  const displayCount = jobs !== null
    ? (adsFromJobs || completedJobs.length > 0 ? adsFromJobs : null)
    : (brief.ads_count ?? null);

  const fetchData = useCallback(async () => {
    if (jobs !== null) return;
    setFetching(true);
    try {
      const j = await adGenerationApi.listJobsForBrief(brief.brief_id);
      setJobs(j);
      const firstCompleted = j.find((x) => isCompletedStatus(x.status));
      const pid = firstCompleted?.output_pack_id ?? firstCompleted?.pack_id;
      if (pid) {
        const r = await fetch(
          `${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(pid)}/tracks/final_ad`,
        );
        if (r.ok) {
          const data = (await r.json()) as { assets?: ThumbAsset[] };
          setThumbs(data.assets ?? []);
        }
      }
    } catch {
      setJobs([]);
    } finally {
      setFetching(false);
    }
  }, [brief.brief_id, jobs]);

  const showPopover = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // w-72 = 18rem ≈ 288px; height varies with thumbnails — use a generous estimate
    const popW = 288;
    const popH = 300;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    // Prefer below the chip so the card reads as anchored to the row (above felt "floating" mid-viewport)
    let top: number;
    if (spaceBelow >= popH || spaceBelow >= spaceAbove) {
      top = rect.bottom + margin;
    } else {
      top = rect.top - popH - margin;
    }
    top = Math.max(margin, Math.min(top, vh - popH - margin));

    const idealLeft = rect.left + rect.width / 2 - popW / 2;
    const left = Math.max(margin, Math.min(idealLeft, vw - popW - margin));

    setPopPos({ top, left });
    setVisible(true);
    void fetchData();
  }, [fetchData]);

  const startShow = () => {
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
    hoverTimer.current = window.setTimeout(showPopover, 250);
  };
  const startHide = () => {
    if (hoverTimer.current) { window.clearTimeout(hoverTimer.current); hoverTimer.current = null; }
    hideTimer.current = window.setTimeout(() => setVisible(false), 150);
  };
  const cancelHide = () => {
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
  };

  // Hide entirely once we know there are no packs
  if (confirmedEmpty) return null;

  // Chip appearance: neutral (slate) before data loads, emerald once we have packs
  const hasKnownPacks = jobs !== null && (completedJobs.length > 0 || activeJobs.length > 0);
  const chipClass = hasKnownPacks
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
    : 'text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100';

  return (
    <>
      {/* ── Inline chip ── */}
      <span
        ref={triggerRef}
        onMouseEnter={startShow}
        onMouseLeave={startHide}
        className={`inline-flex items-center gap-1 text-[10px] font-semibold border px-2 py-0.5 rounded-full cursor-default select-none transition-colors ${chipClass}`}
      >
        <ImageIcon size={9} />
        {displayCount !== null
          ? `${displayCount} ad${displayCount !== 1 ? 's' : ''}`
          : 'Assets'}
        {activeJobs.length > 0 && (
          <span className="relative flex h-1.5 w-1.5 ml-0.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-400" />
          </span>
        )}
      </span>

      {/* ── Portal popover ── */}
      {visible &&
        createPortal(
          <div
            ref={popoverRef}
            onMouseEnter={cancelHide}
            onMouseLeave={startHide}
            style={{ top: popPos.top, left: popPos.left }}
            className="fixed z-[9999] w-72 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
              <div className="flex items-center gap-1.5">
                <Sparkles size={11} className="text-emerald-500" />
                <span className="text-[11px] font-bold text-slate-800">
                  {fetching ? 'Loading packs…' : (
                    <>
                      {displayCount !== null ? `${displayCount} ads` : 'Asset Packs'}
                      {completedJobs.length > 0 && (
                        <span className="text-slate-400 font-normal ml-1">
                          · {completedJobs.length} pack{completedJobs.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
              {activeJobs.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                  <Zap size={8} />
                  {activeJobs.length} generating
                </span>
              )}
            </div>

            {/* Thumbnail strip */}
            <div className="px-4 py-3">
              {fetching ? (
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <Loader2 size={12} className="animate-spin" />
                  Fetching asset packs…
                </div>
              ) : thumbs.length > 0 ? (
                <div className="grid grid-cols-4 gap-1.5">
                  {thumbs.slice(0, 4).map((t) => {
                    const url = t.thumbnail_url ?? t.media_url ?? null;
                    return (
                      <div
                        key={t.asset_id}
                        className="aspect-square rounded-lg overflow-hidden bg-slate-100"
                      >
                        {url ? (
                          <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={12} className="text-slate-300" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {thumbs.length > 4 && (
                    <div className="aspect-square rounded-lg bg-slate-100 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-slate-400">+{thumbs.length - 4}</span>
                    </div>
                  )}
                </div>
              ) : jobs !== null && completedJobs.length > 0 ? (
                <p className="text-[11px] text-slate-400 italic">Thumbnails not available</p>
              ) : jobs !== null && completedJobs.length === 0 && activeJobs.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">No completed packs yet</p>
              ) : null}
            </div>

            {/* Footer CTA */}
            {packPath && (
              <div className="px-4 pb-3">
                <button
                  onClick={() => { setVisible(false); navigate(packPath); }}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition"
                >
                  Open Asset Pack <ChevronRight size={11} />
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Brief Row
// ---------------------------------------------------------------------------

function BriefRow({ brief, onClick }: { brief: GenerationBrief; onClick: () => void }) {
  const sourceInfo = getSourceTypeLabel(brief.provenance?.source_type);
  const quality = brief.quality;
  const playbookCount = brief.playbook_ids?.length ?? 0;
  const isUnlinked = playbookCount === 0;

  return (
    <tr className="hover:bg-slate-50 transition-colors group">
      <td className="px-4 py-3">
        <div className="font-mono text-[11px] text-purple-500">{brief.brief_id}</div>
        <div className="font-medium text-navy-900 mt-0.5 truncate max-w-[200px]">{brief.name}</div>
      </td>
      <td className="px-4 py-3">
        <div className="text-slate-600">
          {brief.audience?.persona_name || brief.persona_id?.replace(/_/g, ' ') || '—'}
        </div>
        {brief.persona_id &&
          brief.persona_id !== (brief.audience?.persona_name || '').toLowerCase().replace(/ /g, '_') && (
            <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
              {brief.persona_id}
            </span>
          )}
      </td>
      <td className="px-4 py-3">
        <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
          {brief.client_uid}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-slate-600 flex items-center gap-1">
          <span>{sourceInfo.icon}</span>
          <span className="text-[10px]">{sourceInfo.label}</span>
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={brief.status} />
      </td>
      <td className="px-4 py-3">
        {quality ? (
          <div className="flex items-center gap-2">
            <QualityBar value={quality.data_completeness} />
            <span className="text-[10px] text-slate-500">{Math.round(quality.confidence_score * 100)}%</span>
          </div>
        ) : (
          <span className="text-slate-300">-</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          {isUnlinked ? (
            <span
              title="Not attached to a playbook"
              className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 bg-slate-50 border border-dashed border-slate-300 px-2 py-0.5 rounded-full"
            >
              <Link2Off size={9} />
              No playbook
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
              <Layers size={9} />
              {playbookCount} playbook{playbookCount !== 1 ? 's' : ''}
            </span>
          )}
          <AssetCountBadge brief={brief} />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {brief.version > 1 && (
            <span className="text-[10px] text-slate-500">v{brief.version}</span>
          )}
          {brief.parent_brief_id && <GitBranch size={10} className="text-slate-400" />}
        </div>
      </td>
      <td className="px-4 py-3" title={brief.updated_at ?? brief.created_at}>
        <div className="text-slate-500">{timeAgo(brief.updated_at ?? brief.created_at)}</div>
        {brief.updated_at && brief.created_at && brief.updated_at !== brief.created_at && (
          <div className="text-[9px] text-slate-300 mt-0.5">
            created {timeAgo(brief.created_at)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right sticky right-0 bg-white group-hover:bg-slate-50 after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-slate-200 transition-colors">
        <button
          onClick={onClick}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 hover:border-teal-300 group-hover:shadow-sm transition-all"
        >
          View
          <ChevronRight size={12} className="opacity-70" />
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Playbook Row
// ---------------------------------------------------------------------------

function PlaybookRow({
  playbook,
  briefCount,
}: {
  playbook: GenerationPlaybook;
  briefCount: number;
}) {
  return (
    <tr className="hover:bg-slate-50 transition-colors group">
      <td className="px-4 py-3">
        <div className="font-mono text-[11px] text-violet-500">{playbook.playbook_id}</div>
        <div className="font-medium text-navy-900 mt-0.5 truncate max-w-[220px]">{playbook.name}</div>
      </td>
      <td className="px-4 py-3">
        <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
          {playbook.client_uid}
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={playbook.status} />
      </td>
      <td className="px-4 py-3">
        {briefCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
            <FileText size={9} />
            {briefCount} brief{briefCount !== 1 ? 's' : ''}
          </span>
        ) : (
          <span className="text-slate-300 text-[10px]">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {playbook.asset_summary?.total_assets != null ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">
            {playbook.asset_summary.total_assets}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-400" title={playbook.created_at}>
        {timeAgo(playbook.created_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          disabled
          title="Playbook view coming soon"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-300 border border-dashed border-slate-200 rounded-lg cursor-not-allowed"
        >
          Open
          <ExternalLink size={10} />
        </button>
      </td>
    </tr>
  );
}

function StatPill({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
      <span className="text-[10px] text-slate-500 uppercase font-bold">{label}</span>
      <span className={clsx('font-bold', accent ? 'text-teal-600' : 'text-navy-900')}>{value}</span>
    </div>
  );
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <div className="animate-pulse">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4 border-b border-slate-100">
          <div className="h-4 bg-slate-200 rounded w-24" />
          <div className="h-4 bg-slate-200 rounded w-32" />
          <div className="h-4 bg-slate-200 rounded w-20" />
          <div className="h-4 bg-slate-200 rounded w-24" />
          <div className="h-4 bg-slate-200 rounded w-16" />
          <div className="h-4 bg-slate-200 rounded w-16" />
          <div className="h-4 bg-slate-200 rounded w-24" />
          <div className="h-4 bg-slate-200 rounded w-12" />
          <div className="h-4 bg-slate-200 rounded w-16" />
          <div className="h-4 bg-slate-200 rounded w-12 ml-auto" />
        </div>
      ))}
    </div>
  );
}

// Keep StatPill exported for any external consumers
export { StatPill };

